/**
 * T308 · Anon vs authed branching · initial-mint bootstrap orchestrator.
 *
 * Spec 05 §675 + §1311. The bootstrap path for the very first JWT mint
 * per app load. Resolves the bearer token from the platform's auth
 * source (web: cookie-bound session via `/v1/session`; RN: stored bearer
 * from `expo-secure-store`).
 *
 *   - bearer resolves to a valid session, or the web request carries a valid
 *     httpOnly session cookie → user mint (4 h TTL, generated anon roots plus
 *     generated user-leaf allow_sub entries).
 *   - otherwise → anon mint (2 h TTL, generated anon allow_sub entries).
 *
 * After the mint resolves:
 *   1. Write the JWT to `jwtStore`.
 *   2. Schedule the refresh ladder (T305).
 *   3. Acquire the connection (T303).
 *
 * Critical sequencing: the mint MUST resolve BEFORE the first
 * `acquireConnection` is awaited — the authenticator closure throws on
 * `jwtStore.current() === null` (T304). The SDKProvider (phase 13)
 * calls this orchestrator once at root mount.
 *
 * SSR-safe: this module lives under the `/client` barrel only.
 */

import { type MintClientConfig, type MintedCredentials, mintCredentials } from './auth.js';
import { getJwtStore, type SecureStore } from './jwt-store.js';
import { type RefreshOutcome, scheduleRefresh } from './refresh-ladder.js';

/**
 * Caller-supplied bearer-token resolver. Returns the platform's
 * session bearer (web cookie session → `/v1/session` token; RN →
 * secureStore bearer) or `null` if there is no signed-in user.
 */
export type ResolveBearer = () => Promise<string | null>;

/**
 * Caller-supplied connection acquirer. Decoupled from this module so
 * the orchestrator can be unit-tested without touching nats-core.
 */
export type AcquireConnection = () => Promise<void>;

export interface InitialMintOpts {
  readonly mintConfig: MintClientConfig;
  /**
   * Stable network/backend boundary for startup coalescing. Calls in a
   * different realm must mint independently even when their bearer matches.
   */
  readonly realmKey: string;
  readonly resolveBearer: ResolveBearer;
  readonly acquireConnection: AcquireConnection;
  /**
   * Optional RN secureStore for the `restore()` short-circuit. When
   * present and a non-expired JWT is found, the network mint is
   * skipped.
   */
  readonly secureStore?: SecureStore;
  /** Injected clock (defaults to Date.now). */
  readonly now?: () => number;
  /**
   * Mid-session refresh planner armed on the ladder after the first
   * mint. When omitted the ladder is armed with a no-op (bootstrap-only
   * behaviour, preserved for unit tests). Production wiring
   * (`SDKProvider`) injects the real planner from
   * `connectivity/jwt/jwt-refresh.ts` so the pre-expiry timer actually
   * re-mints + reconnects (T306 / T3016).
   */
  readonly runRefresh?: () => Promise<RefreshOutcome>;
}

export type InitialMintKind = 'anon' | 'user' | 'restored';

export interface InitialMintResult {
  readonly kind: InitialMintKind;
  readonly expiresAt: number;
}

const inFlightMints = new Map<string, Map<string | null, Promise<MintedCredentials>>>();

function mintOncePerRealm(
  realmKey: string,
  mintConfig: MintClientConfig,
  bearer: string | null,
): Promise<MintedCredentials> {
  let realm = inFlightMints.get(realmKey);
  const existing = realm?.get(bearer);
  if (existing !== undefined) return existing;

  if (realm === undefined) {
    realm = new Map();
    inFlightMints.set(realmKey, realm);
  }

  const mint = mintCredentials(mintConfig, { bearerToken: bearer });
  realm.set(bearer, mint);

  const clear = (): void => {
    if (realm?.get(bearer) !== mint) return;
    realm.delete(bearer);
    if (realm.size === 0) inFlightMints.delete(realmKey);
  };
  void mint.then(clear, clear);

  return mint;
}

/**
 * Run the bootstrap sequence. Resolves once the WS connection has been
 * acquired (or rejects with the underlying mint error after the retry
 * ladder is exhausted).
 *
 * Order:
 *   1. (RN) restore from secureStore — if the cached JWT is still
 *      valid, set the store and SKIP the network mint.
 *   2. Resolve the optional bearer and trust the backend's returned mint kind
 *      (the web session cookie can promote a bearer-less request to user).
 *   3. jwtStore.set(minted)
 *   4. scheduleRefresh(expiresAt → runRefresh)
 *   5. acquireConnection()
 */
export async function initialMint(opts: InitialMintOpts): Promise<InitialMintResult> {
  const now = opts.now ?? ((): number => Date.now());
  const store = getJwtStore();

  // Step 1 (RN): restore from secureStore.
  if (opts.secureStore !== undefined) {
    await store.restore(opts.secureStore);
    const current = store.current();
    if (current !== null && current.jwt.length > 0) {
      // Cached JWT exists. Caller is responsible for re-checking
      // expiry server-side via the next reconnect; if the broker
      // rejects, the closure form (T304) + refresh ladder (T305) will
      // recover. We schedule the refresh ladder relative to the
      // cached `expiresAt` if known; otherwise we leave scheduling to
      // the post-mint flow that runs on the first refresh.
      await opts.acquireConnection();
      return {
        kind: 'restored',
        // We do not have a fresh expiresAt from secureStore — return 0
        // so callers can detect "unknown" and prefer the network mint
        // if they require a precise schedule. The `jwt-store` itself
        // does not persist `expiresAt`.
        expiresAt: 0,
      };
    }
  }

  // Step 2: resolve the optional bearer. The response decides mint kind.
  const bearer = await opts.resolveBearer();

  // Network mint. `mintCredentials` throws SdkError on retry exhaustion
  // — we propagate so the caller's bootstrap error path can flip the
  // connectivity FSM to DEGRADED_NO_NATS(cause:'mint-failed').
  const minted = await mintOncePerRealm(opts.realmKey, opts.mintConfig, bearer);
  // The backend is authoritative. A web request can omit an Authorization
  // bearer yet still be promoted by its httpOnly session cookie.
  const kind = minted.kind;

  // Step 3: write to jwtStore. Carry `allow_sub` so the provider's
  // wire-subscription effect opens one SUB per minted root (user = 7,
  // anon = 5) instead of defaulting to the anon scope.
  store.set({
    jwt: minted.jwt,
    kind,
    ...(minted.allow_sub !== undefined ? { allowSub: minted.allow_sub } : {}),
  });

  // Step 4: schedule the refresh ladder. `mintCredentials.expiresAt` is
  // in epoch seconds; scheduleRefresh expects epoch milliseconds.
  const expiresAtMs = minted.expiresAt * 1_000;
  scheduleRefresh({
    expiresAt: expiresAtMs,
    now,
    // Production wiring injects the real planner (connectivity/jwt
    // refresh → re-mint + reconnect, T306 / T3016). Absent it, the
    // ladder is armed with a no-op (bootstrap-only / unit-test path).
    runRefresh: opts.runRefresh ?? (async () => ({ success: false })),
  });

  // Step 5: acquire the connection. The authenticator closure now reads
  // a non-null JWT from the store.
  await opts.acquireConnection();

  return {
    kind,
    expiresAt: expiresAtMs,
  };
}
